import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiUsers, FiAward, FiClock, FiCheckCircle, FiTrendingUp, FiBookOpen, FiEdit2, FiX, FiSave } from 'react-icons/fi';
import { getCareerRoster, updateCurrentTierHistory } from '../services/api';
import {
  CAREER_TRACKS,
  GROUP_STYLE,
  formatTierBracket,
} from '../components/CareerBadge';

// Admin-only: every active member's current tier placement, time-in-tier,
// next bracket / next tier eligibility, certifications earned, and modules in
// progress. Server-gated to can_manage_payroll.

function formatDays(days) {
  if (days == null) return '—';
  if (days < 30) return `${days} day${days === 1 ? '' : 's'}`;
  if (days < 365) {
    const m = Math.floor(days / 30);
    return `${m} month${m === 1 ? '' : 's'}`;
  }
  const y = Math.floor(days / 365);
  const remM = Math.floor((days % 365) / 30);
  return remM > 0 ? `${y}y ${remM}m` : `${y} year${y === 1 ? '' : 's'}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

function tierLabel(group, tier, bracket) {
  if (!group || !tier) return 'Unplaced';
  const rate = formatTierBracket(group, tier, bracket);
  return `Tier ${tier} ${bracket || ''}${rate ? ` · ${rate}` : ''}`;
}

function EligibilityCell({ slot }) {
  if (!slot) return <span style={{ color: '#94a3b8' }}>—</span>;
  const color = slot.eligible ? '#16a34a' : '#0891b2';
  const Icon = slot.eligible ? FiCheckCircle : FiClock;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color, fontWeight: 600, fontSize: 13 }}>
        <Icon size={14} />
        {slot.eligible ? 'Eligible now' : `Eligible ${formatDate(slot.eligible_on)}`}
      </div>
      <div style={{ fontSize: 11, color: '#475569' }}>{slot.reason}</div>
    </div>
  );
}

function GroupChip({ group }) {
  if (!group) return <span style={{ color: '#94a3b8' }}>—</span>;
  const style = GROUP_STYLE[group] || { color: '#475569', bg: '#f1f5f9', label: group };
  return (
    <span style={{
      display: 'inline-block',
      background: style.bg,
      color: style.color,
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: 11,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>{style.label}</span>
  );
}

function TrackPill({ track, level }) {
  const c = CAREER_TRACKS[track] || { color: '#475569', bg: '#f1f5f9' };
  return (
    <span title={`${track} L${level}`} style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      background: c.bg,
      color: c.color,
      padding: '2px 6px',
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 600,
      marginRight: 4,
    }}>
      {track.split(' ').map(w => w[0]).join('')} L{level}
    </span>
  );
}

function CertBadge({ c }) {
  const style = CAREER_TRACKS[c.track] || { color: '#475569', bg: '#f1f5f9' };
  return (
    <span title={`${c.title} — ${c.score}%`} style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      background: style.bg,
      color: style.color,
      padding: '2px 6px',
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 600,
      marginRight: 4,
      marginBottom: 4,
      opacity: c.approved ? 1 : 0.6,
    }}>
      <FiAward size={11} />
      {c.track.split(' ').map(w => w[0]).join('')} L{c.level}
    </span>
  );
}

function ProgressBar({ pct }) {
  return (
    <div style={{ width: 80, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: '#3b82f6' }} />
    </div>
  );
}

export default function CareerRoster() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [groupFilter, setGroupFilter] = useState('All');
  const [eligibilityFilter, setEligibilityFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // {member_id, name, current_date, value, note, saving, error}

  const reload = () => {
    return getCareerRoster()
      .then(res => setRows(res.data.roster || []))
      .catch(err => setError(err.response?.data?.error || err.message || 'Failed to load roster.'));
  };

  useEffect(() => {
    let mounted = true;
    getCareerRoster()
      .then(res => { if (mounted) setRows(res.data.roster || []); })
      .catch(err => {
        if (!mounted) return;
        setError(err.response?.data?.error || err.message || 'Failed to load roster.');
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const openEdit = (row) => {
    const cur = row.eligibility?.current || {};
    setEditing({
      member_id: row.member_id,
      name: row.name,
      placement: tierLabel(cur.pay_group, cur.pay_tier, cur.pay_bracket),
      value: cur.entered_at ? cur.entered_at.slice(0, 10) : new Date().toISOString().slice(0, 10),
      note: '',
      saving: false,
      error: '',
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setEditing({ ...editing, saving: true, error: '' });
    try {
      await updateCurrentTierHistory(editing.member_id, {
        entered_at: editing.value,
        note: editing.note || undefined,
      });
      setEditing(null);
      await reload();
    } catch (err) {
      setEditing(prev => prev && {
        ...prev,
        saving: false,
        error: err.response?.data?.error || err.message || 'Failed to update.',
      });
    }
  };

  const filtered = useMemo(() => {
    return rows.filter(r => {
      const cur = r.eligibility?.current || {};
      if (groupFilter !== 'All' && cur.pay_group !== groupFilter) return false;
      if (eligibilityFilter === 'Bracket eligible' && !(r.eligibility?.next_bracket?.eligible)) return false;
      if (eligibilityFilter === 'Tier eligible' && !(r.eligibility?.next_tier?.eligible)) return false;
      if (search && !r.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [rows, groupFilter, eligibilityFilter, search]);

  const stats = useMemo(() => ({
    total: rows.length,
    field: rows.filter(r => r.eligibility?.current?.pay_group === 'Field').length,
    office: rows.filter(r => r.eligibility?.current?.pay_group === 'Office').length,
    bracketEligible: rows.filter(r => r.eligibility?.next_bracket?.eligible).length,
    tierEligible: rows.filter(r => r.eligibility?.next_tier?.eligible).length,
  }), [rows]);

  return (
    <div className="career-roster-page" style={{ padding: '20px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <Link to="/team" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#475569', textDecoration: 'none', fontSize: 14 }}>
          <FiArrowLeft /> Back to Team
        </Link>
      </div>

      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiUsers /> Career Roster
          </h1>
          <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: 14 }}>
            Tier placement, time-in-tier, promotion eligibility, and training progress for every active team member.
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="Active members" value={stats.total} color="#0f172a" />
        <StatCard label="Field group" value={stats.field} color={GROUP_STYLE.Field.color} />
        <StatCard label="Office group" value={stats.office} color={GROUP_STYLE.Office.color} />
        <StatCard label="Bracket eligible" value={stats.bracketEligible} color="#16a34a" icon={FiTrendingUp} />
        <StatCard label="Tier eligible" value={stats.tierEligible} color="#2563eb" icon={FiTrendingUp} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="Search by name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, minWidth: 200 }}
        />
        <select value={groupFilter} onChange={e => setGroupFilter(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14 }}>
          <option value="All">All groups</option>
          <option value="Field">Field group</option>
          <option value="Office">Office group</option>
        </select>
        <select value={eligibilityFilter} onChange={e => setEligibilityFilter(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14 }}>
          <option value="All">All eligibility</option>
          <option value="Bracket eligible">Bracket-eligible now</option>
          <option value="Tier eligible">Tier-eligible now</option>
        </select>
        <span style={{ color: '#64748b', fontSize: 13 }}>
          Showing {filtered.length} of {rows.length}
        </span>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading roster…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>No team members match the current filters.</div>
      ) : (
        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                <th style={th}>Name</th>
                <th style={th}>Group</th>
                <th style={th}>Current placement</th>
                <th style={th}>Time in tier</th>
                <th style={th}>Tracks</th>
                <th style={th}>Next bracket</th>
                <th style={th}>Next tier</th>
                <th style={th}>Certifications</th>
                <th style={th}>In progress</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const cur = r.eligibility?.current || {};
                return (
                  <tr key={r.member_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {r.role}{r.hire_date ? ` · hired ${formatDate(r.hire_date)}` : ''}
                      </div>
                    </td>
                    <td style={td}>
                      <GroupChip group={cur.pay_group} />
                      {r.eligibility?.dual_track && (
                        <span style={{ display: 'inline-block', marginLeft: 4, background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>
                          DUAL
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{tierLabel(cur.pay_group, cur.pay_tier, cur.pay_bracket)}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
                        since {formatDate(cur.entered_at)}
                        {cur.pay_group && (
                          <button
                            type="button"
                            onClick={() => openEdit(r)}
                            title="Edit start date of current tier placement"
                            style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', padding: '0 4px', display: 'inline-flex', alignItems: 'center' }}
                          >
                            <FiEdit2 size={11} />
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={td}>{formatDays(cur.days_in_tier)}</td>
                    <td style={td}>
                      {(r.career_tracks || []).length === 0
                        ? <span style={{ color: '#94a3b8' }}>—</span>
                        : (r.career_tracks || []).map(ct => (
                            <TrackPill key={ct.id} track={ct.career_track} level={ct.career_level} />
                          ))
                      }
                    </td>
                    <td style={td}><EligibilityCell slot={r.eligibility?.next_bracket} /></td>
                    <td style={td}><EligibilityCell slot={r.eligibility?.next_tier} /></td>
                    <td style={td}>
                      {(r.certifications_earned || []).length === 0
                        ? <span style={{ color: '#94a3b8' }}>—</span>
                        : (r.certifications_earned || []).map(c => <CertBadge key={c.module_id} c={c} />)
                      }
                    </td>
                    <td style={td}>
                      {(r.modules_in_progress || []).length === 0
                        ? <span style={{ color: '#94a3b8' }}>—</span>
                        : (r.modules_in_progress || []).map(m => (
                            <div key={m.module_id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, marginBottom: 4 }} title={`${m.title}: ${m.lessons_completed}/${m.lessons_total} lessons`}>
                              <FiBookOpen size={11} style={{ color: '#3b82f6' }} />
                              <span style={{ color: '#475569' }}>{m.track.split(' ').map(w => w[0]).join('')} L{m.level}</span>
                              <ProgressBar pct={m.progress_pct} />
                              <span style={{ color: '#94a3b8' }}>{m.lessons_completed}/{m.lessons_total}</span>
                            </div>
                          ))
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div
          onClick={() => !editing.saving && setEditing(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 8, padding: 24, width: '100%', maxWidth: 460, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Edit time in tier</h2>
              <button
                type="button"
                onClick={() => !editing.saving && setEditing(null)}
                style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 4 }}
              >
                <FiX size={20} />
              </button>
            </div>
            <div style={{ marginBottom: 12, color: '#475569', fontSize: 13 }}>
              <div><strong>{editing.name}</strong></div>
              <div style={{ color: '#64748b' }}>{editing.placement}</div>
            </div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
              Effective from
            </label>
            <input
              type="date"
              value={editing.value}
              max={new Date().toISOString().slice(0, 10)}
              onChange={e => setEditing({ ...editing, value: e.target.value })}
              style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, marginBottom: 12 }}
            />
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
              Note <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              type="text"
              placeholder="e.g. backfilled per 2024 review"
              value={editing.note}
              onChange={e => setEditing({ ...editing, note: e.target.value })}
              style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, marginBottom: 16 }}
            />
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
              This adjusts the start date of the member's current tier placement. Time-in-tier and promotion eligibility update immediately.
            </div>
            {editing.error && (
              <div style={{ background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
                {editing.error}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => !editing.saving && setEditing(null)}
                disabled={editing.saving}
                style={{ padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={editing.saving || !editing.value}
                style={{ padding: '8px 16px', background: '#0891b2', color: 'white', border: 'none', borderRadius: 6, cursor: editing.saving ? 'wait' : 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <FiSave size={14} /> {editing.saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const th = { padding: '10px 12px', fontWeight: 600, fontSize: 12, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 };
const td = { padding: '12px', verticalAlign: 'top' };

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 12, marginBottom: 6 }}>
        {Icon && <Icon size={14} />}
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}
